import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/site/ProductCard";
import useSeo from "@/lib/useSeo";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const isNew = params.get("new") === "1";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "newest";
  const [products, setProducts] = useState(null);
  const title = isNew ? "New Arrivals" : q ? `Search: “${q}”` : category || "Shop All";
  useSeo(isNew ? "New Arrivals" : "Shop All", "Shop the Maison Madison collection — leather outerwear and considered essentials.");

  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: { q: q || undefined, new: isNew || undefined, category: category || undefined, sort } }).then((r) => setProducts(r.data));
  }, [q, isNew, category, sort]);

  const update = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };

  return (
    <div data-testid="shop-page" className="px-5 sm:px-10 lg:px-16 pt-14 sm:pt-20 pb-24">
      <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
        <div className="lg:col-span-7">
          <p className="eyebrow text-[var(--mm-red)] mb-4 rise">Maison Madison</p>
          <h1 data-testid="shop-title" className="font-serif-mm text-5xl sm:text-6xl lg:text-7xl leading-none rise d1">{title}</h1>
        </div>
        <p className="lg:col-span-4 lg:col-start-9 text-sm text-[var(--mm-muted)] rise d2">Small-batch pieces, made to be worn for years. Each design is produced in limited quantities.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-y border-[var(--mm-line)] py-4 mb-12">
        {q && <button data-testid="clear-search-button" onClick={() => update("q", "")} className="eyebrow flex items-center gap-1 text-[var(--mm-red)]">“{q}” <X size={12} /></button>}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[var(--mm-muted)]" data-testid="product-count">{products ? `${products.length} piece${products.length === 1 ? "" : "s"}` : ""}</span>
          <select data-testid="sort-select" value={sort} onChange={(e) => update("sort", e.target.value)} className="eyebrow bg-transparent border border-[var(--mm-line)] px-3 py-2 outline-none">
            <option value="newest">Newest</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
          </select>
        </div>
      </div>
      {products === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-12">{[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[3/4] bg-[var(--mm-sand)] animate-pulse" />)}</div>
      ) : products.length === 0 ? (
        <div data-testid="shop-empty-state" className="py-24"><p className="font-serif-mm text-4xl">Nothing here <em className="text-[var(--mm-red)]">just yet.</em></p><p className="text-sm text-[var(--mm-muted)] mt-4">Try another search or browse all pieces.</p></div>
      ) : (
        <div data-testid="product-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-14">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i % 4} />)}
        </div>
      )}
    </div>
  );
}
